"use client";

import { useEffect } from "react";

const GA_MEASUREMENT_ID = "G-P6Q56PNDMH";

declare global {
  interface Window {
    dataLayer?: any[];
    gtag?: (...args: any[]) => void;
  }
}

// Bootstraps the GA4 dataLayer/gtag queue and downloads gtag.js. The
// `<script>` tag is `async` so it never blocks parsing or paint; the queue
// stub is set up synchronously inside this effect so any caller that fires
// `gtag(...)` (e.g. `GAHelper.event("conversion", ...)` on `/success`) hits
// a real queue instead of `undefined`.
function initGtag() {
  if (typeof window === "undefined") return;
  if (window.gtag) return;

  const w = window as any;
  w.dataLayer = w.dataLayer || [];
  function gtag() {
    // eslint-disable-next-line prefer-rest-params
    w.dataLayer.push(arguments);
  }
  w.gtag = gtag;
  (gtag as any)("js", new Date());
  (gtag as any)("config", GA_MEASUREMENT_ID);

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  const first = document.getElementsByTagName("script")[0];
  first?.parentNode?.insertBefore(script, first);
}

// Loads on mount, deferred to browser idle time when supported. This pairs
// with the `async` `<script>` tag inside `initGtag()` to keep the SDK off
// the LCP critical path while still guaranteeing it loads shortly after the
// page settles — never gated on user interaction, so server-redirect pages
// like `/success` (no clicks before tracking fires) still get the queue
// stubs in time.
function scheduleInit() {
  if (typeof window === "undefined") return;
  const ric = (window as Window & {
    requestIdleCallback?: (cb: () => void) => void;
  }).requestIdleCallback;
  if (ric) {
    ric(initGtag);
  } else {
    setTimeout(initGtag, 0);
  }
}

export default function Gtag() {
  useEffect(() => {
    scheduleInit();
  }, []);

  return null;
}
